import { C, R, F } from '../../lib/designTokens'

// Small presentational pieces shared by the Product Knowledge readers.
// Every text comes from the catalog; English first, Arabic below it (right-to-left).

const sectionLabel = { fontSize: F.label, fontWeight: 700, color: C.sub, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 8 }

export function Bi({ en, ar, style }) {
  if (!en && !ar) return null
  return (
    <div style={style}>
      {en && <div style={{ whiteSpace: 'pre-line' }}>{en}</div>}
      {ar && <div dir="rtl" style={{ marginTop: en ? 2 : 0, whiteSpace: 'pre-line' }}>{ar}</div>}
    </div>
  )
}

export function Section({ title, count, children }) {
  return (
    <div style={{ marginBottom: 22 }}>
      <div style={sectionLabel}>{title}{typeof count === 'number' ? ` (${count})` : ''}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>{children}</div>
    </div>
  )
}

const TONES = { blue: [C.blueBg, C.blue], green: [C.greenBg, C.green], gray: [C.surfaceAlt, C.sub], red: [C.surfaceAlt, C.red] }
export function Tag({ children, tone = 'gray' }) {
  const [bg, fg] = TONES[tone] || TONES.gray
  return (
    <span style={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', padding: '2px 7px', borderRadius: R.tag, background: bg, color: fg, flexShrink: 0 }}>
      {children}
    </span>
  )
}

export function Card({ children, onClick }) {
  const base = { padding: '10px 14px', borderRadius: R.card, border: `1px solid ${C.border}`, background: C.surface, textAlign: 'left' }
  if (!onClick) return <div style={base}>{children}</div>
  return (
    <div role="button" tabIndex={0} onClick={onClick} onKeyDown={e => { if (e.key === 'Enter') onClick() }}
      style={{ ...base, cursor: 'pointer' }}
      onMouseEnter={e => e.currentTarget.style.borderColor = C.blue}
      onMouseLeave={e => e.currentTarget.style.borderColor = C.border}>
      {children}
    </div>
  )
}

function Line({ label, en, ar }) {
  if (!en && !ar) return null
  return (
    <div style={{ fontSize: F.body, color: C.ink2, marginTop: 6, lineHeight: 1.45 }}>
      <div style={{ fontSize: F.caption, fontWeight: 700, color: C.sub }}>{label}</div>
      <Bi en={en} ar={ar} />
    </div>
  )
}

const KIND_LABEL = { tab: 'Tab / تبويب', kpi: 'KPI / مؤشر', column: 'Column / عمود', toolbar: 'Toolbar / شريط الأدوات', property: 'Property / خاصية', module: 'Module / وحدة' }
const RULE_LABEL = {
  tip: ['Tip / نصيحة', 'green'], negative_knowledge: ['Common mistake / خطأ شائع', 'red'], calculation: ['Calculation / حساب', 'gray'],
  alert: ['Alert / تنبيه', 'gray'], validation: ['Validation / تحقق', 'gray'], routing: ['Routing / توجيه', 'gray'],
  restriction: ['Restriction / قيد', 'gray'], limit: ['Limit / حد', 'gray'],
  faq: ['FAQ / سؤال شائع', 'gray'],
}

export function FieldItem({ f, hideKind }) {
  return (
    <Card>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: F.bodyLg, fontWeight: 700, color: C.ink }}>{f.label || f.key}</span>
        {f.value_kind && !hideKind && <Tag>{KIND_LABEL[f.value_kind] || f.value_kind}</Tag>}
        {f.required && <Tag tone="red">Required / إلزامي</Tag>}
      </div>
      <Bi en={f.meaning_en} ar={f.meaning_ar} style={{ fontSize: F.body, color: C.ink2, marginTop: 4, lineHeight: 1.45 }} />
      <Line label="Where the value comes from / مصدر القيمة" en={f.source_en} ar={f.source_ar} />
      <Line label="What it affects / ما يتأثر به" en={f.effect_en} ar={f.effect_ar} />
    </Card>
  )
}

export function ActionItem({ a }) {
  return (
    <Card>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: F.bodyLg, fontWeight: 700, color: C.ink }}>{a.label || a.key}</span>
        {a.status_effect && <Tag tone="green">Sets status: {a.status_effect}</Tag>}
      </div>
      <Bi en={a.effect_en} ar={a.effect_ar} style={{ fontSize: F.body, color: C.ink2, marginTop: 4, lineHeight: 1.45 }} />
      <Line label="Availability / التوفّر" en={a.shown_when_en} ar={a.shown_when_ar} />
      <Line label="Confirmation / التأكيد" en={a.confirmation_en} ar={a.confirmation_ar} />
    </Card>
  )
}

export function RuleItem({ r, onOpen, hideType }) {
  return (
    <Card onClick={onOpen}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>{r.rule_type && !hideType && <Tag tone={(RULE_LABEL[r.rule_type] || [])[1] || 'gray'}>{(RULE_LABEL[r.rule_type] || [r.rule_type])[0]}</Tag>}</div>
      <Bi en={r.statement_en} ar={r.statement_ar} style={{ fontSize: F.body, color: C.ink, marginTop: 4, lineHeight: 1.45, fontWeight: r.rule_type === 'faq' ? 700 : 400 }} />
      <Line label={r.rule_type === 'faq' ? 'Answer / الإجابة' : 'Formula / المعادلة'} en={r.formula_en} ar={r.formula_ar} />
    </Card>
  )
}

export function OutputItem({ o, onOpen }) {
  return (
    <Card onClick={onOpen}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: F.bodyLg, fontWeight: 700, color: C.ink }}>{o.name_en || o.key}</span>
        {o.format && <Tag tone="blue">{o.format}</Tag>}
      </div>
      {o.name_ar && <div dir="rtl" style={{ fontSize: F.body, color: C.sub }}>{o.name_ar}</div>}
      <Bi en={o.description_en} ar={o.description_ar} style={{ fontSize: F.body, color: C.ink2, marginTop: 4, lineHeight: 1.45 }} />
      <Line label="Where you see it / أين تراه" en={o.where_visible_en} ar={o.where_visible_ar} />
    </Card>
  )
}

export function ExceptionItem({ e, onOpen }) {
  return (
    <Card onClick={onOpen}>
      {e.message && <div style={{ fontSize: F.caption, color: C.sub, fontStyle: 'italic', marginBottom: 2 }}>“{e.message}”</div>}
      <Bi en={e.meaning_en} ar={e.meaning_ar} style={{ fontSize: F.bodyLg, fontWeight: 700, color: C.ink, lineHeight: 1.4 }} />
      <Line label="Why it happens / لماذا يحدث" en={e.cause_en} ar={e.cause_ar} />
      <Line label="What to do / ما العمل" en={e.what_to_do_en} ar={e.what_to_do_ar} />
      <Line label="Who can fix it / من يستطيع الإصلاح" en={e.who_can_fix_en} ar={e.who_can_fix_ar} />
    </Card>
  )
}

export function CapabilityItem({ c }) {
  return (
    <Card>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <span style={{ fontSize: F.body, fontWeight: 700, color: C.ink }}>{c.name || c.key}</span>
        {(c.roles || []).map(r => <Tag key={r} tone="blue">{r}</Tag>)}
        {c.role_scope && <Tag>{c.role_scope}</Tag>}
      </div>
      <Bi en={c.enables_en} ar={c.enables_ar} style={{ fontSize: F.body, color: C.ink2, marginTop: 4 }} />
      <Bi en={c.note_en} ar={c.note_ar} style={{ fontSize: F.caption, color: C.sub, marginTop: 4, fontStyle: 'italic' }} />
    </Card>
  )
}

export function SharedControlItem({ s }) {
  return (
    <Card>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        {(s.labels || []).map(l => <span key={l} style={{ fontSize: F.bodyLg, fontWeight: 700, color: C.ink }}>{l}</span>)}
        {s.is_global && <Tag tone="green">Everywhere / في كل مكان</Tag>}
      </div>
      <Bi en={s.behavior_en} ar={s.behavior_ar} style={{ fontSize: F.body, color: C.ink2, marginTop: 4, lineHeight: 1.45 }} />
    </Card>
  )
}

// Screenshots with a bilingual caption. An image that fails to load (file not copied yet) is simply not shown.
export function Screenshots({ items, imageUrl }) {
  if (!items || items.length === 0) return null
  return (
    <div style={{ display: 'grid', gap: 14 }}>
      {items.map((it) => (
        <figure key={it.file} data-shot={it.file} style={{ margin: 0 }}>
          <img src={imageUrl(it.file)} alt={it.en} loading="lazy" onError={(e) => { const f = e.currentTarget.closest('figure'); if (f) f.style.display = 'none' }}
            style={{ maxWidth: '100%', border: `1px solid ${C.border}`, borderRadius: R.card, display: 'block' }} />
          <figcaption style={{ fontSize: F.caption, color: C.sub, marginTop: 4, lineHeight: 1.4 }}>
            <div>{it.en}</div><div dir="rtl">{it.ar}</div>
          </figcaption>
        </figure>
      ))}
    </div>
  )
}
